/**
 * <MenuPopover label> — a ··· button that opens a lifted menu beside it.
 * Opens to the right, flips left when it would overflow the window. Closes on
 * outside click, Escape, and any click on a menu item.
 *
 *   <MenuPopover label="More actions for X">
 *     <MenuItem onClick={...}>Delete</MenuItem>
 *   </MenuPopover>
 */
import React, { useEffect, useRef, useState } from 'react';
import Card from './Card';

const MENU_WIDTH_PX = 272; // w-64 plus the 8px gap
const ITEM_SELECTOR = '[role="menuitem"]';

export function MenuItem({ onClick, children, danger = false, className = '', ...rest }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={`w-full text-left text-sm px-3 py-2 rounded-[10px] hover:bg-hl-soft disabled:opacity-50 disabled:cursor-not-allowed focus-ring ${danger ? 'text-status-failed' : ''} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

export default function MenuPopover({ label, trigger = '···', children, className = '' }) {
  const [open, setOpen] = useState(false);
  const [left, setLeft] = useState(false);
  const triggerRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('click', onOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('click', onOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setLeft(rect.right + MENU_WIDTH_PX > window.innerWidth);
    }
    setOpen((o) => !o);
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <button
        type="button"
        aria-label={label}
        aria-haspopup="true"
        aria-expanded={open}
        ref={triggerRef}
        onClick={toggle}
        className="font-extrabold text-pencil tracking-wider px-1 focus-ring rounded"
      >
        {trigger}
      </button>
      {open && (
        <Card
          variant="lifted"
          role="menu"
          onClick={(e) => { if (e.target.closest(ITEM_SELECTOR)) setOpen(false); }}
          className={`absolute top-0 z-20 w-64 p-2 flex flex-col gap-1 ${left ? 'right-full mr-2' : 'left-full ml-2'}`}
        >
          {children}
        </Card>
      )}
    </div>
  );
}
